/**
 * Browser-side persistence of the workspace:
 * - localStorage: which sessions are open, which laps/diagrams are selected, sidebar state.
 * - IndexedDB: the bytes of every opened file, so a refresh can reopen local files too.
 * Every call tolerates storage being unavailable (private mode, quota) and degrades to no-op.
 */

/** How acceleration is shown on the racing lines: coloured lanes, throttle and brake points, or not at all. */
export type AccelMode = 'off' | 'lanes' | 'points'

export interface ViewState {
  /** Selected laps across sessions, `${sessionId}:${lapIndex}`, in pick order */
  selected: string[]
  reference?: string
  /** True once the user picked the reference by hand; otherwise it follows the best selected lap */
  referenceManual?: boolean
  channelNames: string[]
  showDelta: boolean
  /** The per-section summary block; on unless switched off */
  showSummary?: boolean
  /** Older saves only: the racing-line display is no longer remembered, it always opens on the points */
  accelMode?: AccelMode
  /** Older saves only, as above */
  showAccel?: boolean
  /** The per-tyre traction circle; on unless switched off */
  showTraction?: boolean
}

export interface SessionRef {
  id: string
  name: string
  cloudKey?: string
}

export interface LocalState {
  sessions: SessionRef[]
  view: ViewState
  sidebarOpen?: boolean
}

export const EMPTY_VIEW: ViewState = {
  selected: [],
  channelNames: [],
  showDelta: true,
  showSummary: true,
}

const LS_KEY = 'vmc.racestudio.v1'

export function loadLocal(): LocalState | null {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LocalState>
    return {
      sessions: Array.isArray(parsed.sessions) ? parsed.sessions : [],
      view: { ...EMPTY_VIEW, ...(parsed.view ?? {}) },
      sidebarOpen: parsed.sidebarOpen,
    }
  } catch {
    return null
  }
}

/** Forget the saved workspace and every cached file on this device (account deletion). */
export function clearLocal(): void {
  try {
    localStorage.removeItem(LS_KEY)
  } catch {
    /* storage may be unavailable */
  }
  void pruneFiles([])
}

export function saveLocal(state: LocalState): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state))
  } catch {
    /* storage unavailable */
  }
}

// ---------- IndexedDB file cache ----------
const DB_NAME = 'vmc-racestudio'
const STORE = 'files'

interface StoredFile {
  id: string
  name: string
  cloudKey?: string
  bytes: ArrayBuffer
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null)
    try {
      const req = indexedDB.open(DB_NAME, 1)
      req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' })
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

function run<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve(undefined)
        try {
          const tx = db.transaction(STORE, mode)
          const req = fn(tx.objectStore(STORE))
          req.onsuccess = () => resolve(req.result)
          req.onerror = () => resolve(undefined)
          tx.oncomplete = () => db.close()
        } catch {
          resolve(undefined)
        }
      }),
  )
}

export function putFile(file: StoredFile): Promise<unknown> {
  return run('readwrite', (s) => s.put(file))
}

export function getFile(id: string): Promise<StoredFile | undefined> {
  return run<StoredFile>('readonly', (s) => s.get(id))
}

export function deleteFile(id: string): Promise<unknown> {
  return run('readwrite', (s) => s.delete(id))
}

/** Drop cached files that are no longer referenced, to keep the cache bounded. */
export async function pruneFiles(keepIds: string[]): Promise<void> {
  const keys = await run<IDBValidKey[]>('readonly', (s) => s.getAllKeys())
  if (!keys) return
  const keep = new Set(keepIds)
  for (const k of keys) if (typeof k === 'string' && !keep.has(k)) await deleteFile(k)
}
